import { supabase } from '../lib/supabaseClient';
import type { Canal, TiendaElegible, VisitaDeRuta } from '../domain/ruta';

const BUCKET = 'photos';

/**
 * La ruta del supervisor contra la base (migración 0092).
 *
 * Dos cosas que valen más que el resto de este archivo:
 *
 * **El registro pasa SOLO por la RPC.** `ruta_visitas` no tiene policy de INSERT a propósito: si el
 * navegador pudiera insertar, mandaría sus propias banderas y `registro_tardio` dejaría de
 * significar algo. Lo mismo que ya se hizo con el canje y con el cierre de turno.
 *
 * **La foto se sube ANTES y con el mismo `client_uuid`.** Así el reintento reescribe el mismo
 * objeto en vez de estrenar ruta y dejar huérfana la anterior — el error que costó las fotos del
 * levantamiento (ver `uploadVisitaPhoto`).
 */

/**
 * Los puntos de venta de la maestra, filtrados por CANAL.
 *
 * El filtro no es cosmético. Lo pidió el usuario después de ver Makro y Plaza Vea ofrecidos en el
 * formulario de bodegas: *«cuando es bodegas son puntos de venta, puede ser mercado o puede ser
 * una bodega de calle… no debería salir Makro ni Plaza Vea ni Tottus»*. Un desplegable con 54
 * puntos de tres canales distintos hace que elegir mal sea lo más probable.
 *
 * Sin canales pedidos devuelve todo: es lo correcto para una visita a secas, donde el supervisor
 * va a ver a su gente y la tienda puede ser cualquiera.
 */
export async function listPuntosDeVenta(canales: readonly Canal[] = []): Promise<TiendaElegible[]> {
  let q = supabase
    .from('pdv')
    .select('id, name, chain, district, canal')
    .eq('active', true)
    .order('name');
  if (canales.length > 0) q = q.in('canal', canales as string[]);
  const { data, error } = await q;
  if (error) throw new Error(`No se pudo cargar la lista de puntos de venta: ${error.message}`);
  return (data ?? []).map((p) => {
    const r = p as { id: string; name: string; chain: string | null; district: string | null };
    return {
      pdvId: r.id,
      nombre: r.name,
      detalle: [r.chain, r.district].filter((x) => x !== null && x !== '').join(' · ') || null,
    };
  });
}

/**
 * Las tiendas del estudio de canal moderno (`pg_tiendas`, 0070).
 *
 * Sigue existiendo aparte de `pdv` porque la maestra no tiene los supermercados del estudio: al
 * 28-ago solo había 5 filas de relleno, todas inactivas. `pdvId` viene lleno en las enlazadas.
 */
export async function listTiendasDeCanalModerno(): Promise<TiendaElegible[]> {
  const { data, error } = await supabase
    .from('pg_tiendas')
    .select('id, nombre, cadena, pdv_id, activo')
    .eq('activo', true)
    .order('nombre');
  if (error) throw new Error(`No se pudo cargar la lista de supermercados: ${error.message}`);
  return (data ?? []).map((p) => {
    const r = p as { nombre: string; cadena: string | null; pdv_id: string | null };
    return { pdvId: r.pdv_id, nombre: r.nombre, detalle: r.cadena };
  });
}

/** Sube la foto de la visita. Devuelve la ruta que se guarda en la fila. */
export async function subirFotoDeVisita(clientUuid: string, foto: Blob): Promise<string> {
  const path = `ruta/${clientUuid}.jpg`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, foto, {
    contentType: 'image/jpeg',
    upsert: true, // reintento: mismo path, mismo contenido
  });
  if (error) throw new Error(`No se pudo subir la foto: ${error.message}`);
  return path;
}

/**
 * Registra la visita. `clientUuid` lo elige quien llama —y es el mismo con el que se subió la
 * foto—, así que reintentar devuelve el registro que ya existe en vez de crear otro.
 */
export async function registrarVisitaDeRuta(v: VisitaDeRuta, clientUuid: string): Promise<string> {
  const { data, error } = await supabase.rpc('registrar_visita_de_ruta', {
    p_payload: {
      client_uuid: clientUuid,
      fecha: v.fecha,
      ocurrio_en: v.ocurrioEn,
      pdv_id: v.pdvId ?? '',
      tienda_texto: v.tiendaTexto,
      campaign_id: v.campaignId ?? '',
      estudio_id: v.estudioId ?? '',
      impulsadora_id: v.impulsadoraId ?? '',
      visito_impulsadora: v.visitoImpulsadora,
      instrumentos: v.instrumentos,
      notas: v.notas,
      lat: v.lat === null ? '' : String(v.lat),
      lng: v.lng === null ? '' : String(v.lng),
      accuracy_m: v.accuracyM === null ? '' : String(v.accuracyM),
      foto_path: v.fotoPath ?? '',
    },
  });
  if (error) {
    // eslint-disable-next-line no-console -- la causa real, para poder depurar desde el celular
    console.error('registrar_visita_de_ruta falló:', error);
    throw new Error(`No se pudo registrar la visita: ${error.message}`);
  }
  return data as string;
}

/** Una visita como se muestra, tanto en el celular como en la bandeja. */
export interface VisitaEnLista {
  id: string;
  fecha: string;
  registradoEn: string;
  ocurrioEn: string | null;
  supervisor: string;
  tienda: string;
  campania: string | null;
  impulsadora: string | null;
  visitoImpulsadora: boolean;
  instrumentos: string[];
  notas: string | null;
  fotoPath: string | null;
  registroTardio: boolean;
  sinGps: boolean;
  tiendaFueraDeMaestra: boolean;
}

const SELECT_VISITA =
  'id, fecha, registrado_en, ocurrio_en, tienda_texto, notas, foto_path,'
  + ' registro_tardio, sin_gps, tienda_fuera_de_maestra, visito_impulsadora, instrumentos,'
  + ' supervisor:profiles!ruta_visitas_worker_id_fkey(first_names, last_names),'
  + ' impulsadora:profiles!ruta_visitas_impulsadora_id_fkey(first_names, last_names),'
  + ' pdv:pdv(name), campaign:campaigns(client, brand)';

type RawVisita = {
  id: string; fecha: string; registrado_en: string; ocurrio_en: string | null;
  tienda_texto: string | null; notas: string | null; foto_path: string | null;
  visito_impulsadora: boolean; instrumentos: string[] | null;
  registro_tardio: boolean; sin_gps: boolean; tienda_fuera_de_maestra: boolean;
  supervisor: { first_names: string; last_names: string } | null;
  impulsadora: { first_names: string; last_names: string } | null;
  pdv: { name: string } | null;
  campaign: { client: string; brand: string } | null;
};

function aVisita(r: RawVisita): VisitaEnLista {
  return {
    id: r.id,
    fecha: r.fecha,
    registradoEn: r.registrado_en,
    ocurrioEn: r.ocurrio_en,
    supervisor: r.supervisor ? `${r.supervisor.last_names} ${r.supervisor.first_names}` : '—',
    tienda: r.pdv?.name ?? r.tienda_texto ?? '—',
    campania: r.campaign ? `${r.campaign.client} — ${r.campaign.brand}` : null,
    impulsadora: r.impulsadora
      ? `${r.impulsadora.last_names} ${r.impulsadora.first_names}`
      : null,
    visitoImpulsadora: r.visito_impulsadora,
    instrumentos: r.instrumentos ?? [],
    notas: r.notas,
    fotoPath: r.foto_path,
    registroTardio: r.registro_tardio,
    sinGps: r.sin_gps,
    tiendaFueraDeMaestra: r.tienda_fuera_de_maestra,
  };
}

/**
 * Las visitas de un rango. **La RLS decide qué filas llegan**: el supervisor ve las suyas y la
 * oficina las de todos. No se filtra por persona acá a propósito — un filtro del cliente que
 * "protege" datos es un filtro que alguien puede quitar desde la consola del navegador.
 */
export async function listVisitasDeRuta(desde: string, hasta: string): Promise<VisitaEnLista[]> {
  const { data, error } = await supabase
    .from('ruta_visitas')
    .select(SELECT_VISITA)
    .gte('fecha', desde)
    .lte('fecha', hasta)
    .eq('estado', 'registrada')
    .order('fecha', { ascending: false })
    .order('registrado_en', { ascending: false });
  if (error) throw new Error(`No se pudieron cargar las visitas: ${error.message}`);
  return ((data ?? []) as unknown as RawVisita[]).map(aVisita);
}

/**
 * Las tiendas que este supervisor tiene a cargo, en CUALQUIER campaña donde esté asignado
 * (`campaign_pdv_supervisors`, spec 2026-09-24). Es el denominador de la cobertura semanal.
 *
 * «Mi ruta» no separa por campaña —el supervisor ve una sola lista de tiendas—, así que acá se
 * unen todas sus asignaciones y se deduplica por tienda: la misma tienda puede aparecer en más
 * de una campaña activa y no por eso hay que mostrarla dos veces.
 *
 * Puede volver vacío —no toda tienda activa tiene supervisor asignado—, y en ese caso la
 * pantalla no muestra porcentaje en vez de mostrar 0 %.
 */
export async function misTiendasACargo(): Promise<{ id: string; nombre: string }[]> {
  const { data: sesion } = await supabase.auth.getUser();
  const uid = sesion.user?.id;
  if (uid === undefined) return [];
  const { data, error } = await supabase
    .from('campaign_pdv_supervisors')
    .select('pdv:pdv_id(id, name)')
    .eq('profile_id', uid)
    .order('created_at');
  if (error) return [];
  const vistas = new Map<string, string>();
  for (const row of (data ?? []) as unknown as { pdv: { id: string; name: string } }[]) {
    vistas.set(row.pdv.id, row.pdv.name);
  }
  return [...vistas.entries()]
    .map(([id, nombre]) => ({ id, nombre }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre));
}

/**
 * Borra la visita de verdad. Exige motivo, que queda en la constancia interna de `audit_log`.
 *
 * ⚠️ La RPC la NIEGA si la visita tiene fichas de levantamiento colgando: `pg_fichas` y
 * `bod_fichas` la referencian con `NO ACTION`, y el cuestionario de campo —una hora de trabajo— no
 * es lo que el supervisor se equivoco al registrar. El mensaje del servidor explica cuantas fichas
 * hay y que hay que resolverlas primero, asi que se muestra tal cual y no se traduce.
 */
export async function borrarVisitaDeRuta(id: string, motivo: string): Promise<void> {
  const { error } = await supabase.rpc('borrar_visita_de_ruta', { p_id: id, p_motivo: motivo });
  if (error) throw new Error(error.message);
}
